// AI Prompt Templates — live playground for the news synthesis system prompt.
//
// Layout:
//   1. "Currently in production" card — shows the active prompt (read-only,
//      collapsed by default) plus the last-edited timestamp.
//   2. "Draft" card — editable textarea pre-loaded with the current value.
//   3. "Test against" card — toggle between a real event group from prod
//      and a pasted-text fixture, plus a Run button.
//   4. Output card — appears after a test run with title/summary/body and
//      token usage. Stays visible while the user iterates.
//
// Iteration loop is ~10-30s per run. "Save & publish" hits PUT /editor/prompts
// which invalidates the synthesis cache, so the next live pipeline tick
// uses the new prompt without waiting on the 60s TTL.
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Select, Textarea } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

const PROMPT_KEY = "news_synthesis_system";

interface TemplateResponse {
  key: string;
  body: string;
  updated_at: string | null;
  updated_by: string | null;
  fallback: string | null;
  uses_fallback: boolean;
}

interface RecentEventGroup {
  id: string;
  source_count: number;
  status: string;
  created_at: string;
  label_title: string;
  label_source: string | null;
}

interface SynthesisResult {
  title?: string;
  summary?: string;
  body?: string;
  rawResponse: string;
  tokensIn: number;
  tokensOut: number;
}

type SourceMode = "event_group" | "text";

export default function PromptTemplatesPage() {
  const [template, setTemplate] = useState<TemplateResponse | null>(null);
  const [draft, setDraft] = useState("");
  const [groups, setGroups] = useState<RecentEventGroup[]>([]);
  const [sourceMode, setSourceMode] = useState<SourceMode>("event_group");
  const [groupId, setGroupId] = useState<string>("");
  const [textTitle, setTextTitle] = useState("");
  const [textBody, setTextBody] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [result, setResult] = useState<SynthesisResult | null>(null);
  const [showProduction, setShowProduction] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tpl, recent] = await Promise.all([
        api<TemplateResponse>(`/editor/prompts/${PROMPT_KEY}`),
        api<{ groups: RecentEventGroup[] }>("/editor/prompts/sources/recent"),
      ]);
      setTemplate(tpl);
      setDraft(tpl.body || tpl.fallback || "");
      setGroups(recent.groups);
      if (recent.groups.length > 0) setGroupId(recent.groups[0].id);
    } catch (e: any) {
      setError(e.message ?? "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  async function handleSave() {
    if (!draft.trim()) {
      setError("Prompt body cannot be empty");
      return;
    }
    setSaving(true);
    setError(null);
    setSavedNotice(null);
    try {
      const updated = await api<{ key: string; body: string; updated_at: string }>(
        `/editor/prompts/${PROMPT_KEY}`,
        { method: "PUT", body: { body: draft } },
      );
      setTemplate({
        key: updated.key,
        body: updated.body,
        updated_at: updated.updated_at,
        updated_by: null,
        fallback: template?.fallback ?? null,
        uses_fallback: false,
      });
      setSavedNotice("Saved & published — next synthesis tick uses this prompt.");
    } catch (e: any) {
      setError(e.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    if (!draft.trim()) {
      setError("Prompt body cannot be empty");
      return;
    }
    if (sourceMode === "event_group" && !groupId) {
      setError("Pick an event group");
      return;
    }
    if (sourceMode === "text" && !textBody.trim()) {
      setError("Paste some text to synthesize from");
      return;
    }

    setTesting(true);
    setError(null);
    setResult(null);

    const source =
      sourceMode === "event_group"
        ? { type: "event_group" as const, id: groupId }
        : {
            type: "text" as const,
            title: textTitle.trim() || undefined,
            body: textBody,
          };

    try {
      const r = await api<SynthesisResult>(
        `/editor/prompts/${PROMPT_KEY}/test`,
        { method: "POST", body: { body: draft, source } },
      );
      setResult(r);
    } catch (e: any) {
      setError(e.message ?? "Test synthesis failed");
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Spinner size={20} />
      </div>
    );
  }

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>
          AI Prompt Templates
        </h1>
        <div style={{ color: "var(--text-muted)", fontSize: 13 }}>
          Iterate on the news synthesis system prompt. Test runs don't write
          to drafts.
        </div>
      </header>

      {/* Currently in production */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <CardTitle>Currently in production</CardTitle>
          <Button variant="secondary" size="sm" onClick={() => setShowProduction((v) => !v)}>
            {showProduction ? "Hide" : "Show full prompt"}
          </Button>
        </div>
        <CardSubtle style={{ marginTop: 4 }}>
          {template?.uses_fallback
            ? "DB row is empty — production falls back to the in-code default."
            : `Last updated ${formatTimestamp(template?.updated_at)}`}
        </CardSubtle>
        {showProduction && (
          <pre style={styles.readonlyPre}>
            {template?.uses_fallback
              ? template?.fallback ?? ""
              : template?.body ?? ""}
          </pre>
        )}
      </Card>

      {/* Draft */}
      <Card style={{ marginBottom: 16 }}>
        <CardTitle>Draft</CardTitle>
        <CardSubtle style={{ marginBottom: 12 }}>
          Edit freely, test below, then save when you're happy.
        </CardSubtle>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={20}
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        />
        <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save & publish"}
          </Button>
          <Button variant="secondary" onClick={() => setDraft(template?.body || template?.fallback || "")}>
            Reset to current
          </Button>
          {savedNotice && (
            <span style={{ color: "var(--success)", fontSize: 13 }}>
              {savedNotice}
            </span>
          )}
        </div>
      </Card>

      {/* Test against */}
      <Card style={{ marginBottom: 16 }}>
        <CardTitle>Test against</CardTitle>
        <CardSubtle style={{ marginBottom: 12 }}>
          Pick a real event group from the pipeline, or paste your own text.
        </CardSubtle>

        <div style={styles.toggleRow}>
          <Button
            variant={sourceMode === "event_group" ? "primary" : "secondary"}
            size="sm"
            onClick={() => setSourceMode("event_group")}
          >
            Real event group
          </Button>
          <Button
            variant={sourceMode === "text" ? "primary" : "secondary"}
            size="sm"
            onClick={() => setSourceMode("text")}
          >
            Pasted text
          </Button>
        </div>

        {sourceMode === "event_group" ? (
          <div style={{ marginTop: 12 }}>
            <Label>Recent event groups</Label>
            {groups.length === 0 ? (
              <CardSubtle>No event groups in the pipeline yet.</CardSubtle>
            ) : (
              <Select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {truncate(g.label_title, 80)} — {g.source_count} sources · {formatTimestamp(g.created_at)}
                  </option>
                ))}
              </Select>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <Label>Title (optional)</Label>
            <Input
              value={textTitle}
              onChange={(e) => setTextTitle(e.target.value)}
              placeholder="Headline of the test article"
              style={{ marginBottom: 12 }}
            />
            <Label>Body</Label>
            <Textarea
              value={textBody}
              onChange={(e) => setTextBody(e.target.value)}
              rows={10}
              placeholder="Paste source material the prompt should work from."
            />
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <Button onClick={handleTest} disabled={testing}>
            {testing ? (
              <>
                <Spinner size={12} /> &nbsp; Synthesizing…
              </>
            ) : (
              "Run synthesis"
            )}
          </Button>
        </div>
      </Card>

      {/* Output */}
      {result && (
        <Card>
          <CardTitle>Output</CardTitle>
          <CardSubtle style={{ marginBottom: 12 }}>
            Tokens used: {result.tokensIn} in / {result.tokensOut} out — not
            saved to drafts.
          </CardSubtle>

          <div style={styles.outputBlock}>
            <Label>Title</Label>
            <div style={styles.outputText}>{result.title ?? "(failed to parse — see raw response below)"}</div>
          </div>
          <div style={styles.outputBlock}>
            <Label>Summary</Label>
            <div style={styles.outputText}>{result.summary ?? "—"}</div>
          </div>
          <div style={styles.outputBlock}>
            <Label>Body</Label>
            <div style={{ ...styles.outputText, whiteSpace: "pre-wrap" }}>
              {result.body ?? "—"}
            </div>
          </div>

          {!result.title && (
            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: "pointer", color: "var(--text-secondary)", fontSize: 13 }}>
                Raw response (parse failed)
              </summary>
              <pre style={styles.readonlyPre}>{result.rawResponse}</pre>
            </details>
          )}
        </Card>
      )}

      {error && (
        <div style={styles.errorBox} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

function formatTimestamp(ts: string | null | undefined): string {
  if (!ts) return "never";
  const d = new Date(ts);
  return d.toLocaleString();
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

const styles: Record<string, React.CSSProperties> = {
  readonlyPre: {
    marginTop: 12,
    padding: 12,
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-secondary)",
    fontSize: 12,
    lineHeight: 1.5,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    whiteSpace: "pre-wrap",
    maxHeight: 400,
    overflow: "auto",
  },
  toggleRow: {
    display: "flex",
    gap: 8,
  },
  outputBlock: {
    marginBottom: 12,
  },
  outputText: {
    padding: 12,
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-primary)",
    fontSize: 14,
    lineHeight: 1.6,
  },
  errorBox: {
    position: "fixed",
    bottom: 16,
    right: 16,
    padding: "10px 14px",
    background: "var(--danger)",
    color: "#fff",
    borderRadius: "var(--radius-sm)",
    fontSize: 13,
    maxWidth: 320,
    boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
  },
};
